/**
 * Commissions feature routes.
 *
 *   /commissions                     → redirects to the Air tab
 *   /commissions/air                 → the commission review workbench (inside the shell)
 *   /commissions/contract-viewer     → the pop-out viewer window (standalone, no shell)
 *
 * A Hotel tab later is a sibling of `air` here plus one page-registry entry.
 *
 * GATED, THEN LAZY. Every element sits inside CommissionsGate (reviewer role
 * AND the `commissions.workbench.enabled` flag, fail-closed). The workbench,
 * the data-source seam, the fixture and pdf.js are all behind React.lazy /
 * dynamic import INSIDE the gate, so (a) the shell bundle carries none of
 * them, and (b) without role + flag the chunks are never even fetched. A
 * closed gate renders NOTHING rather than redirecting: a redirect to '/' would
 * loop through the landing page for a reviewer-only user while the flag is off.
 *
 * NOT THE SECURITY BOUNDARY. The commission API enforces review:commissions on
 * every endpoint; this only decides what the browser shows.
 */
import { lazy, Suspense } from 'react';
import { Navigate, Route } from 'react-router';
import type {
  FeatureRouteList,
  FullBleedPathList,
} from '@/features/routes-registry';
import { CommissionsGate } from './gate/CommissionsGate';
import { CommissionsLayout } from './module/CommissionsLayout';
import { ModuleLoading } from './module/ModuleLoading';
import { COMMISSIONS_AIR_PATH, COMMISSIONS_BASE } from './paths';

const AirReviewPage = lazy(() => import('./module/AirReviewPage'));
const ContractViewerWindow = lazy(
  () => import('./module/ContractViewerWindow'),
);

export const commissionsRoutes: FeatureRouteList = [
  <Route
    element={
      <CommissionsGate>
        <CommissionsLayout />
      </CommissionsGate>
    }
    key="commissions"
    path="commissions"
  >
    <Route
      element={<Navigate replace to={COMMISSIONS_AIR_PATH} />}
      index
      key="commissions-index"
    />
    <Route
      element={
        <Suspense fallback={<ModuleLoading />}>
          <AirReviewPage />
        </Suspense>
      }
      key="commissions-air"
      path="air"
    />
  </Route>,
];

/** Routes mounted inside the auth gate but OUTSIDE the shell (no rail, no tabs). */
export const commissionsStandaloneRoutes: FeatureRouteList = [
  <Route
    element={
      <CommissionsGate>
        <Suspense fallback={<ModuleLoading />}>
          <ContractViewerWindow />
        </Suspense>
      </CommissionsGate>
    }
    key="commissions-contract-viewer"
    path="commissions/contract-viewer"
  />,
];

/** The workbench owns its columns and scrolling, like chat. */
export const commissionsFullBleedPaths: FullBleedPathList = [COMMISSIONS_BASE];
